<template>
  <KekkleBottomSheet v-model:open="isOpen" shadow="purple" padding="normal">
    <div class="flex h-full flex-col dark:text-slate-200">
      <!-- Header with Back Button -->
      <div class="mb-4 flex items-center">
        <button
          v-if="currentStep !== 'list'"
          class="mr-3 rounded-lg p-2 dark:text-slate-200"
          @click="goBack"
        >
          <span class="text-2xl">←</span>
        </button>
        <h2 class="text-xl font-bold dark:text-slate-200">
          {{ currentTitle }}
        </h2>
      </div>

      <!-- Step: Groups List -->
      <div v-if="currentStep === 'list'">
        <!-- Groups List -->
        <div class="mb-4 grow overflow-y-auto">
          <p
            v-for="group in Array.from(groupsStore.userGroups.values())"
            :key="group._id"
            translate="no"
            class="mb-2 cursor-pointer rounded-sm border-2 border-black-dark bg-white p-3 shadow-yellow-hard-sm hover:shadow-none dark:bg-kekkle-dark-light dark:text-slate-200"
            @click="selectGroup(group._id)"
          >
            {{ group.name }}
          </p>
        </div>
        <div
          v-if="
            userStore.userData &&
            userStore.userData.groups &&
            userStore.userData.groups.length < 20
          "
          class="mb-4 flex gap-3"
        >
          <default-button class="flex-1" @click="currentStep = 'create'">
            <p class="font-medium">
              ✨ {{ $t("global.inputs.labels.make_group") }}
            </p>
          </default-button>
          <default-button class="flex-1" @click="currentStep = 'join'">
            <p class="font-medium">
              🔗 {{ $t("global.inputs.labels.join_group") }}
            </p>
          </default-button>
        </div>
      </div>

      <!-- Step: Create New Group -->
      <div v-else-if="currentStep === 'create'">
        <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {{ $t("pages.createselectgroup.only_name_needed") }}
        </p>
        <text-input
          v-model="newGroupName"
          class="my-4"
          :label="$t('global.inputs.labels.group_name') + '*'"
          :placeholder="$t('global.inputs.placeholders.group_name')"
          :error-message="$t('global.inputs.errors.group_name')"
          :has-error="!newGroupNameValidated"
        ></text-input>
        <default-button
          :loading="status.creatingNewGroup"
          :disabled="!newGroupNameValidated"
          class="w-full"
          @click="saveGroup"
        >
          <p class="font-medium">{{ $t("global.inputs.labels.make_group") }}</p>
        </default-button>
      </div>

      <!-- Step: Join Existing Group -->
      <div v-else-if="currentStep === 'join'">
        <p class="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {{ $t("pages.createselectgroup.join_existing_group") }}
        </p>
        <text-input
          v-model="joinToken"
          class="my-4"
          :placeholder="$t('global.inputs.placeholders.group_token')"
          :label="$t('global.inputs.labels.group_token') + '*'"
          :has-error="!tokenValid"
          :error-message="$t('global.inputs.errors.group_token')"
        >
        </text-input>
        <default-button
          :disabled="!tokenValid || status.creatingNewGroup"
          class="w-full"
          @click="joinGroup()"
        >
          <p class="font-medium">{{ $t("global.inputs.labels.join_group") }}</p>
        </default-button>
      </div>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { useNotificationsStore } from "@/stores/modules/notifications/notifications";
import { setErrorToast } from "@/helpers/notifications/toast";
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@/helpers/firebase/firebase";
import { ImageSizes } from "@/enums/images-sizes";
import * as Sentry from "@sentry/vue";
import KekkleBottomSheet from "./ui/KekkleBottomSheet.vue";
import DefaultButton from "./Buttons/DefaultButton.vue";
import TextInput from "./Inputs/TextInput.vue";
import { useAuthStore } from "../stores/modules/auth/auth.ts";

const router = useRouter();
const { t } = useI18n();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const notificationsStore = useNotificationsStore();

const isOpen = defineModel<boolean>("open", { required: true });

type Step = "list" | "create" | "join";

const currentStep = ref<Step>("list");
const joinToken = ref("");
const newGroupName = ref("");
const status = ref({
  creatingNewGroup: false,
});

const currentTitle = computed(() => {
  switch (currentStep.value) {
    case "list":
      return "🏘️ " + t("pages.group.your_groups");
    case "create":
      return "✨ " + t("global.inputs.labels.make_group");
    case "join":
      return "🔗 " + t("pages.group.join_existing_group");
    default:
      return t("pages.group.your_groups");
  }
});

function goBack() {
  currentStep.value = "list";
  // Clear form data when going back
  joinToken.value = "";
  newGroupName.value = "";
}

// Reset to list view when sheet opens
watch(isOpen, (newValue) => {
  if (newValue) {
    currentStep.value = "list";
    joinToken.value = "";
    newGroupName.value = "";
  }
});

const tokenValid = computed(() => {
  return joinToken.value && joinToken.value.length >= 6;
});

const newGroupNameValidated = computed(() => {
  return (
    newGroupName.value &&
    newGroupName.value.length < 100 &&
    newGroupName.value.length > 2
  );
});

async function joinGroup() {
  notificationsStore.setHasFetched(false);
  await router.push({
    name: "InviteLinkFollowup",
    query: { token: joinToken.value },
  });
}

async function selectGroup(groupId: string) {
  // Close the bottom sheet immediately for instant feedback
  isOpen.value = false;

  try {
    // Optimistically switch group (updates store immediately, syncs to Firestore in background)
    await userStore.switchActiveGroup(groupId);

    // Reset notification fetch flag so new group notifications are fetched
    notificationsStore.setHasFetched(false);

    // Download group picture in background (don't await)
    // Quote and photo memories are derived automatically from onSnapshot listeners
    groupsStore.downloadGroupPictureData(ImageSizes.Thumb);
  } catch (e) {
    console.error("Failed to switch group:", e);
    Sentry.captureException(e);
    // Error toast is already shown by switchActiveGroup
  }
}

async function saveGroup() {
  if (!newGroupNameValidated.value) {
    setErrorToast({ message: t("global.inputs.errors.group_name") });
    return;
  }
  status.value.creatingNewGroup = true;
  try {
    const createGroupFunction = httpsCallable(firebaseFunctions, "createGroup");
    await createGroupFunction({ name: newGroupName.value });
    notificationsStore.setHasFetched(false);
    try {
      await authStore.refreshAuthIdToken();
      await userStore.refreshUserData();
      await groupsStore.refreshAllUserGroupsData();
      isOpen.value = false;
      newGroupName.value = "";
      try {
        await notificationsStore.registerMessageToken();
      } catch {
        //
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  } catch (e) {
    Sentry.captureException(e);
    setErrorToast({
      message: t("pages.createselectgroup.create_group_failed"),
    });
  } finally {
    status.value.creatingNewGroup = false;
  }
}
</script>
